import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "./useAuth";
import { getCoaRequest, submitCoa, updateCoa, updateCoaApprovals } from "./ftoService";
import { easternToday } from "./useFtoForm";
import { awayToWorked, workedToAway, awayFromNote, fetchDayHours, pretty, DAY_START, DAY_END } from "./coaAway";

const EMPTY_COA = {
    empId: "",
    startDate: easternToday(),  // today "yyyy-MM-dd" — Go-live parity (2026-09-24), ATT#29: Eastern date
    endDate: "",
    modifiedStartTime: "",
    modifiedEndTime: "",
    // Owner 2026-10-12: the person enters the time they are AWAY; the two fields above
    // (the hours they will work, what the record and payroll keep) are computed from it.
    awayFrom: "",
    awayUntil: "",
    employeeComment: "",
    supervisorNote: "",
    hrNote: "",
    supervisorApproval: null,
    hrApproval: null,
};

const useCoaForm = ({ coaId = 0 } = {}) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const isNew = coaId === 0;

    const [form, setForm] = useState({ ...EMPTY_COA, empId: auth.empId });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [isSupervisorOf, setIsSupervisorOf] = useState(false);
    // Owner 2026-10-15: a hourly person's own hours (Team Work Hours) are the day the
    // away window is measured against; null = the company day 8:00 AM to 4:30 PM.
    const [dayHours, setDayHours] = useState(null);
    useEffect(() => {
        let alive = true;
        fetchDayHours(form.empId, form.startDate).then((h) => { if (alive) setDayHours(h); });
        return () => { alive = false; };
    }, [form.empId, form.startDate]);

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                setLoading(true);
                const { data } = await getCoaRequest(coaId);
                setIsSupervisorOf(data.isSupervisorOf ?? false);
                setForm({
                    empId: String(data.empId),
                    startDate: data.startDate?.split("T")[0] ?? "",
                    endDate: data.endDate?.split("T")[0] ?? "",
                    modifiedStartTime: data.modifiedStartTime ?? "",
                    modifiedEndTime: data.modifiedEndTime ?? "",
                    // the away window the person typed (note in the comment); else the best guess from the stored hours
                    awayFrom: awayFromNote(data.employeeComment)?.awayFrom ?? workedToAway(data.modifiedStartTime, data.modifiedEndTime)?.awayFrom ?? "",
                    awayUntil: awayFromNote(data.employeeComment)?.awayUntil ?? workedToAway(data.modifiedStartTime, data.modifiedEndTime)?.awayUntil ?? "",
                    employeeComment: data.employeeComment ?? "",
                    supervisorNote: data.supervisorNote ?? "",
                    hrNote: data.hrNote ?? "",
                    supervisorApproval: data.supervisorApproval ?? null,
                    hrApproval: data.hrApproval ?? null,
                });
            } catch {
                toast.error("Failed to load COA request.");
            } finally {
                setLoading(false);
            }
        })();
    }, [coaId]);

    const isOwnRequest = form.empId === auth.empId;
    const canEditEmployee = isNew ? true : isOwnRequest;
    const canEditSupervisor = !isNew && (auth.isSuperUser || isSupervisorOf);
    // Go-live parity (2026-09-24), ATT#17: the HR decision is HR / IT Admin only.
    const canEditHR = !isNew && (auth.isHR || auth.isItAdmin);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Go-live parity (2026-09-24), ATT#6: a supervisor may file a NEW COA for
    // one of their active direct reports (DirectReportPicker).
    const selectEmployee = (empId, empName) => {
        if (!empId) return;
        setForm((f) => ({ ...f, empId, empName: empId === auth.empId ? undefined : empName }));
    };

    // The hours the person will work, from the away window (null = nothing usable yet).
    const worked = awayToWorked(form.awayFrom, form.awayUntil, dayHours?.start, dayHours?.end);
    const workedPreview = worked ? `${pretty(worked.start)} to ${pretty(worked.end)}` : "";
    const dayHoursText = dayHours
        ? `${isOwnRequest ? "your" : "their"} own hours ${pretty(dayHours.start)} to ${pretty(dayHours.end)}`
        : `normal day ${pretty(DAY_START)} to ${pretty(DAY_END)}`;
    const validate = () => {
        if (!form.startDate) return "Date is required.";
        // Owner 2026-10-12: the away window is what the person enters; it must be a real
        // window inside the day. The server keeps the 2-hour limit on the hours it stores.
        if (!form.awayFrom || !form.awayUntil)
            return "Enter the time you will be away: from and until.";
        if (!worked) return "The 'away until' time must be after the 'away from' time.";
        if (!form.employeeComment.trim()) return "Employee comment is required.";
        return null;
    };

    const handleSubmit = async () => {
        const err = validate();
        if (err) { toast.warning(err); return; }
        try {
            setSaving(true);

            const awayNote = `[Away ${pretty(form.awayFrom)} - ${pretty(form.awayUntil)}]`;
            const comment = (form.employeeComment || "").includes("[Away ")
                ? form.employeeComment
                : `${awayNote} ${form.employeeComment || ""}`.trim();
            const payload = {
                empId: form.empId,
                startDate: form.startDate,
                endDate: form.endDate || null,
                modifiedStartTime: worked ? worked.start : (form.modifiedStartTime || null),
                modifiedEndTime: worked ? worked.end : (form.modifiedEndTime || null),
                employeeComment: comment,
            };

            console.log("🚀 COA Submit Payload:", JSON.stringify(payload, null, 2));

            if (isNew) {
                const res = await submitCoa(payload);
                console.log("✅ COA Submit Response:", res.data);
                // Go-live parity (2026-09-24), ATT#6: filed for a direct report.
                toast.success(form.empId !== auth.empId
                    ? "COA request filed — your supervisor approval is recorded and HR has been notified."
                    : "COA request submitted.");
                navigate("/attendance");
            } else {
                if (isOwnRequest) await updateCoa(coaId, payload);
                // Owner 2026-10-12: only a decider sends approvals (same fix as FTO).
                if (canEditSupervisor || canEditHR) await updateCoaApprovals(coaId, {
                    supervisorNote: form.supervisorNote ?? "",
                    hrNote: form.hrNote ?? "",
                    supervisorApproval: form.supervisorApproval ?? null,
                    hrApproval: form.hrApproval ?? null,
                    hrOverrideSupervisor: form.hrOverrideSupervisor ?? false,   // HR override (2026-07)
                });
                toast.success("COA request updated.");
                navigate("/attendance");
            }
        } catch (error) {
            console.error("❌ Status:", error?.response?.status);
            console.error("❌ Response:", JSON.stringify(error?.response?.data, null, 2));
            // Round 2c: surface the server's actual reason (same fix as FTO).
            toast.error(
                error?.response?.data?.message
                || (typeof error?.response?.data === "string" ? error.response.data : null)
                || "Failed to save COA request.");
        } finally {
            setSaving(false);
        }
    };

    return {
        form, setField, loading, saving, isNew, auth,
        canEditEmployee, canEditSupervisor, canEditHR,
        handleSubmit,
        selectEmployee,    // Go-live parity (2026-09-24), ATT#6
        workedPreview,     // owner 2026-10-12: "Hours that day" from the away window
        dayHoursText,      // owner 2026-10-15: the day the away window is measured against
    };
};

export default useCoaForm;